import React, { useState, useEffect } from "react";
import { useIntl } from "react-intl";

import { Row } from "@masterteam/mt-design-react";
import AdminPage from "../AdminPage";
import CardList from "../CardList";
import DeleteModal from "../DeleteModal";
import Form from "./Form";

import { ReactComponent as DeleteIcon } from "../../assets/images/delete.svg";
import api from "../../providers/api";

import { addToast } from "../../helpers";

import "./style.scss";

const initialModalState = {
  isOpen: false,
  data: null,
};

function KPIConfig() {
  const intl = useIntl();
  const isAr = intl.locale === "ar";
  const [loading, setLoading] = useState(true);
  const [typesList, setTypesList] = useState([]);
  const [selectedType, setSelectedType] = useState(0);
  const [groups, setGroups] = useState([]);
  const [deleteModal, setDeleteModal] = useState(initialModalState);
  const [deleteLoading, setDeleteLoading] = useState(false);

  function onClickNew() {
    setSelectedType(-1);
  }

  async function getKpiTypes() {
    setLoading(true);
    try {
      const res = await api.kpiType.getAll();
      setTypesList(res?.Data || []);
    } catch (err) {
      addToast.error(err.message, {
        toastId: "get-users-error",
      });
    } finally {
      setLoading(false);
    }
  }

  async function getGroups() {
    try {
      const res = await api.group.getAll();

      if (res.StatusCode === "Success") {
        setGroups(res.Data || []);
      }
    } catch (err) {
      addToast.error(err.message, {
        toastId: "get-users-error",
      });
    }
  }

  useEffect(() => {
    getKpiTypes();
    getGroups();
  }, []);

  async function handleUpdate(data, id) {
    if (id === -1) {
      setTypesList((currentList) => [...currentList, data]);
    } else {
      setTypesList((currentList) => {
        const temp = [...currentList];
        const index = currentList.findIndex((item) => item.KPITypeID === id);
        if (index !== -1) {
          temp[index] = data;
        }
        return temp;
      });
    }
    setSelectedType(0);
  }

  async function onDeleteType(id) {
    try {
      setDeleteLoading(true);
      const res = await api.kpiType.delete(id);

      if (res?.StatusCode === "Success") {
        setTypesList((currentList) => {
          const temp = [...currentList];
          const index = currentList.findIndex((item) => item.KPITypeID === id);
          if (index !== -1) {
            temp.splice(index, 1);
          }
          return temp;
        });
        setDeleteModal(initialModalState);
        addToast.success("Deleted the KPI Type successfully");
      }
      if (res?.StatusCode === "fail") {
        addToast.error(res?.StatusMessage, {
          toastId: "get-users-error",
        });
      }
    } catch (err) {
      addToast.error("Unable to delete the KPI Type");
    } finally {
      setDeleteLoading(false);
    }
  }

  function onTypeSelect(type) {
    setSelectedType(type?.KPITypeID);
  }

  return (
    <AdminPage
      title={intl.formatMessage({ id: "kpi-type.kpi-types-configuration" })}
    >
      <Row size="large" className="kpi-config__content">
        <CardList
          data={typesList}
          title={intl.formatMessage(
            { id: "common.list" },
            { value: intl.formatMessage({ id: "common.kpi-types" }) }
          )}
          isLoading={loading}
          nameKey={isAr ? "ArabicName" : "EnglishName"}
          valueKey="KPITypeID"
          selectedItem={selectedType}
          onClickItem={onTypeSelect}
          headerActions={[
            {
              key: "add",
              label: intl.formatMessage({ id: "common.add" }),
              fixed: true,
              onClick: onClickNew,
            },
          ]}
          actions={[
            {
              key: "delete",
              variant: "error",
              Icon: DeleteIcon,
              "tooltip-position": "buttom",
              tooltip: intl.formatMessage({ id: "common.delete" }),
              onClick: (item) => {
                setDeleteModal({ isOpen: true, data: item.KPITypeID });
              },
              disabled: (item) => !item.IsDeletable,
            },
          ]}
        />
        {selectedType ? (
          <Form
            key={selectedType}
            callback={handleUpdate}
            selectedType={selectedType}
            groups={groups}
          />
        ) : null}
      </Row>

      {deleteModal.isOpen ? (
        <DeleteModal
          isOpen={deleteModal.isOpen}
          title={intl.formatMessage({ id: "common.kpi-type" })}
          closeModal={() => setDeleteModal(initialModalState)}
          handleDelete={() => onDeleteType(deleteModal.data)}
          ButtonStatus={deleteLoading}
        />
      ) : null}
    </AdminPage>
  );
}

export default KPIConfig;
